import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6, default as _styled7 } from "styled-components";
import React, { useRef } from "react";
import { takeFirst } from "~/features/app/utils";
import { Headline } from "~/features/ui/headline";
import { type Mode } from "~/features/ui/get-theme-style";
import { VideoControls } from "~/features/ui/video/video-controls";
import { Button } from "~/features/ui/button";
import { Link } from "~/features/ui/link";
import { isLinkValid } from "~/features/ui/link/utils";
import { hasItems } from "~/features/app/utils";
import { Tag } from "~/features/ui/tag";
import { composeStegaCleanLabel, cleanStegaForTextTransform } from "~/features/sanity/stega";
import ArrowRight from "~/public/icons/arrow-right.svg";
import ArrowTopRight from "~/public/icons/arrow-top-right.svg";
export function isInlineVideo(media: unknown): boolean {
  if (!media || typeof media !== "object") return false;
  return "videoUrl" in media && !!media.videoUrl || "videoAsset" in media && hasItems((media as {
    videoAsset?: unknown;
  }).videoAsset);
}
type PlainLink = Record<string, unknown> & {
  href?: string;
  newWindow?: boolean;
  text?: React.ReactNode;
};
type SectionDataAttrs = Record<`data-${string}`, string | undefined>;
function getPlainLinkIcon(link?: PlainLink | null) {
  if (!link) {
    return undefined;
  }
  return link.newWindow ? ArrowTopRight : ArrowRight;
}
function getTextValue(value: React.ReactNode) {
  return typeof value === "string" ? value.trim() : "";
}
function getPlainText(value?: string | null) {
  return value ? cleanStegaForTextTransform(value).replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim() : undefined;
}
function isGenericLinkText(text: string) {
  return /^(learn|read|view|see)\s+more$/i.test(text) || /^(click\s+)?here$/i.test(text);
}
function getContextualLinkText(link?: PlainLink | null, headline?: string | null) {
  const text = getTextValue(link?.text);
  const context = getPlainText(headline);
  if (!text || !isGenericLinkText(text) || !context) {
    return undefined;
  }
  return {
    label: composeStegaCleanLabel([text, "about", context]),
    text,
    context
  };
}
export type BannerBlockViewProps = {
  anchor?: Array<{
    id?: string | null;
    name?: string | null;
  }> | {
    id?: string | null;
    name?: string | null;
  } | null;
  hasTopDivider?: boolean | null;
  headline?: string | null;
  isHighlighted?: boolean | null;
  isVideo?: boolean;
  link?: PlainLink | Array<PlainLink | null | undefined> | null;
  media?: (videoRef: React.RefObject<HTMLVideoElement | null>) => React.ReactNode;
  mode?: string | null;
  sectionProps?: React.ComponentPropsWithoutRef<"section"> & SectionDataAttrs;
  tag?: string | null;
  text?: React.ReactNode;
};
export function BannerBlockView({
  anchor,
  hasTopDivider = true,
  headline,
  isHighlighted = false,
  isVideo = false,
  link,
  media,
  mode = "light",
  sectionProps,
  tag,
  text
}: BannerBlockViewProps) {
  const linkItem = takeFirst(link);
  const icon = getPlainLinkIcon(linkItem);
  const contextualLinkText = getContextualLinkText(linkItem, headline);
  const videoRef = useRef<HTMLVideoElement>(null);
  return <_StyledSection {...sectionProps} data-theme={mode as Mode} id={takeFirst(anchor)?.id || undefined} data-anchor-name={takeFirst(anchor)?.name || undefined} $_css={[{
    "position": "relative",
    "backgroundColor": "var(--bg-secondary)",
    "padding": "var(--spacing-md)",
    "color": "var(--text-secondary)",
    "@media (min-width: 1024px)": {
      "padding": "var(--spacing-xl)"
    }
  }, hasTopDivider && {
    "borderTopWidth": "1px",
    "borderColor": "var(--stroke-primary)"
  }, isHighlighted && {
    "backgroundColor": "var(--white-static)"
  }]}>
      <_StyledDiv $_css2={[{
      "position": "relative",
      "display": "grid",
      "minHeight": "68rem",
      "gridTemplateColumns": "repeat(1, minmax(0, 1fr))",
      "overflow": "hidden",
      "borderRadius": "4px",
      "@media (min-width: 1024px)": {
        "aspectRatio": "1520/720",
        "minHeight": "auto",
        "gridTemplateColumns": "repeat(2, minmax(0, 1fr))"
      }
    }]}>
        <_StyledDiv2>{media?.(videoRef)}</_StyledDiv2>

        <_StyledDiv3 $_css3={[{
        "position": "relative",
        "zIndex": "10",
        "display": "flex",
        "flexDirection": "column",
        "alignItems": "flex-start",
        "justifyContent": "flex-end",
        "gap": "var(--spacing-md)",
        "paddingLeft": "var(--spacing-md)",
        "paddingRight": "var(--spacing-md)",
        "paddingTop": "var(--spacing-xl)",
        "paddingBottom": "var(--spacing-xl)",
        "--tw-text-opacity": "1",
        "color": "rgb(255 255 255 / var(--tw-text-opacity))",
        "@media (min-width: 1024px)": {
          "paddingLeft": "var(--spacing-xl)",
          "paddingRight": "var(--spacing-xl)",
          "paddingTop": "var(--spacing-xl)",
          "paddingBottom": "var(--spacing-xl)"
        }
      }]}>
          {tag && <Tag variant="blur">{tag}</Tag>}
          {headline && <_StyledHeadline type="h2" text={headline} />}
          {text}
          <_StyledDiv4>
            {isVideo && <VideoControls size="medium" variant="tertiary" videoRef={videoRef} />}
            {isLinkValid(linkItem) && <Button as={Link} {...linkItem} aria-label={contextualLinkText?.label} variant="secondary" icon={icon}>
                {contextualLinkText ? <>
                    {contextualLinkText.text}
                    <_StyledSpan> about {contextualLinkText.context}</_StyledSpan>
                  </> : undefined}
              </Button>}
          </_StyledDiv4>
        </_StyledDiv3>
      </_StyledDiv>
    </_StyledSection>;
}
var _StyledSection = /*#__PURE__*/_styled("section").withConfig({
  componentId: "sc-sf-f2csby-0"
})`${p => p.$_css}`;
var _StyledDiv = _styled2("div").withConfig({
  componentId: "sc-sf-f2csby-1"
})`${p => p.$_css2}`;
var _StyledDiv2 = _styled3("div").withConfig({
  componentId: "sc-sf-f2csby-2"
})({
  "position": "absolute",
  "inset": "0"
});
var _StyledDiv3 = _styled4("div").withConfig({
  componentId: "sc-sf-f2csby-3"
})`${p => p.$_css3}`;
var _StyledHeadline = _styled5(Headline).withConfig({
  componentId: "sc-sf-f2csby-4"
})({
  "maxWidth": "22ch",
  "fontSize": "2.8rem",
  "lineHeight": "1.2",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "fontSize": "5.4rem",
    "lineHeight": "1.3",
    "letterSpacing": "0.054000000000000006rem",
    "fontWeight": "400"
  }
});
var _StyledDiv4 = _styled6("div").withConfig({
  componentId: "sc-sf-f2csby-5"
})({
  "display": "flex",
  "flexWrap": "wrap",
  "gap": "0.8rem"
});
var _StyledSpan = /*#__PURE__*/_styled7("span").withConfig({
  displayName: "banner___StyledSpan",
  componentId: "sc-guizvn-0"
})({
  "position": "absolute",
  "width": "1px",
  "height": "1px",
  "padding": "0",
  "margin": "-1px",
  "overflow": "hidden",
  "clip": "rect(0, 0, 0, 0)",
  "whiteSpace": "nowrap",
  "borderWidth": "0"
});